import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, XCircle, Wrench, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";

interface ValidationIssue {
  id: string;
  holdingId: string;
  holding: string;
  jurisdiction: string;
  issue: string;
  description: string;
  severity: "high" | "medium" | "low";
  suggestedFix: string;
  lastUpdated: string;
}

export function ValidationTab() {
  const { orgId, familyId } = useTenant();
  const [filterSeverity, setFilterSeverity] = useState("all");
  const [filterJurisdiction, setFilterJurisdiction] = useState("all");

  // Fetch holdings with their regulatory tags
  const { data: holdingsData, isLoading } = useQuery({
    queryKey: ['validation-holdings', orgId, familyId],
    queryFn: async () => {
      if (!orgId) return [];

      // Fetch holdings
      let holdingsQuery = supabase
        .from('holdings')
        .select('*')
        .eq('org_id', orgId);

      if (familyId) {
        holdingsQuery = holdingsQuery.eq('family_id', familyId);
      }

      const { data: holdings, error: holdingsError } = await holdingsQuery;
      if (holdingsError) throw holdingsError;

      if (!holdings || holdings.length === 0) return [];

      // Fetch all regulatory tags for these holdings (all jurisdictions)
      const holdingIds = holdings.map(h => h.id);
      const { data: holdingTags, error: tagsError } = await supabase
        .from('holding_regulatory_tags')
        .select('*, regulatory_tags(*)')
        .in('holding_id', holdingIds);

      if (tagsError) throw tagsError;

      // Group tags by holding and jurisdiction
      const tagsByHolding: Record<string, Record<string, typeof holdingTags>> = {};
      holdingTags?.forEach(ht => {
        if (!tagsByHolding[ht.holding_id]) {
          tagsByHolding[ht.holding_id] = {};
        }
        const jurisdiction = ht.jurisdiction || 'MAS';
        if (!tagsByHolding[ht.holding_id][jurisdiction]) {
          tagsByHolding[ht.holding_id][jurisdiction] = [];
        }
        tagsByHolding[ht.holding_id][jurisdiction].push(ht);
      });

      return holdings.map(h => ({
        ...h,
        tagsByJurisdiction: tagsByHolding[h.id] || {}
      }));
    },
    enabled: !!orgId
  });

  // Compute validation issues from holdings data
  const issues = useMemo((): ValidationIssue[] => {
    if (!holdingsData) return [];

    const result: ValidationIssue[] = [];
    const now = new Date();
    const jurisdictions = ['MAS', 'FATCA', 'CRS'];

    holdingsData.forEach(holding => {
      const holdingName = holding.name || holding.symbol || 'Unknown Holding';

      jurisdictions.forEach(jurisdiction => {
        const tags = holding.tagsByJurisdiction[jurisdiction] || [];

        // Check for untagged holdings (high severity)
        if (tags.length === 0) {
          result.push({
            id: `${holding.id}-${jurisdiction}-untagged`,
            holdingId: holding.id,
            holding: holdingName,
            jurisdiction,
            issue: 'Untagged',
            description: `No ${jurisdiction} regulatory tags assigned`,
            severity: 'high',
            suggestedFix: `Assign ${jurisdiction} asset class tag`,
            lastUpdated: holding.updated_at ? new Date(holding.updated_at).toISOString().split('T')[0] : 'N/A'
          });
        } else {
          // Check for expired tags (medium severity)
          tags.forEach((tag: { id: string; regulatory_tags?: { effective_to?: string; label?: string; code?: string } }) => {
            if (tag.regulatory_tags?.effective_to) {
              const expiryDate = new Date(tag.regulatory_tags.effective_to);
              if (expiryDate < now) {
                result.push({
                  id: `${holding.id}-${jurisdiction}-expired-${tag.id}`,
                  holdingId: holding.id,
                  holding: holdingName,
                  jurisdiction,
                  issue: 'Expired Tag',
                  description: `${tag.regulatory_tags.label || 'Tag'} expired on ${expiryDate.toISOString().split('T')[0]}`,
                  severity: 'medium',
                  suggestedFix: 'Update to current taxonomy version',
                  lastUpdated: holding.updated_at ? new Date(holding.updated_at).toISOString().split('T')[0] : 'N/A'
                });
              }
            }
          });

          // Check for multiple asset class tags (potential conflict - high severity)
          const assetClassTags = tags.filter((t: { regulatory_tags?: { code?: string } }) =>
            t.regulatory_tags?.code?.includes('_AC_')
          );
          if (assetClassTags.length > 1) {
            const tagLabels = assetClassTags.map((t: { regulatory_tags?: { label?: string; code?: string } }) => t.regulatory_tags?.label || t.regulatory_tags?.code).join(', ');
            result.push({
              id: `${holding.id}-${jurisdiction}-conflict`,
              holdingId: holding.id,
              holding: holdingName,
              jurisdiction,
              issue: 'Conflicting Tags',
              description: `Multiple asset class tags: ${tagLabels}`,
              severity: 'high',
              suggestedFix: 'Keep only one primary asset class tag',
              lastUpdated: holding.updated_at ? new Date(holding.updated_at).toISOString().split('T')[0] : 'N/A'
            });
          }

          // Check for missing geography tag when asset class is tagged (low severity for MAS)
          if (jurisdiction === 'MAS') {
            const hasAssetClass = tags.some((t: { regulatory_tags?: { code?: string } }) => t.regulatory_tags?.code?.includes('_AC_'));
            const hasGeo = tags.some((t: { regulatory_tags?: { code?: string } }) => t.regulatory_tags?.code?.includes('_GEO_'));
            if (hasAssetClass && !hasGeo) {
              result.push({
                id: `${holding.id}-${jurisdiction}-missing-geo`,
                holdingId: holding.id,
                holding: holdingName,
                jurisdiction,
                issue: 'Missing Geography Tag',
                description: 'Asset class tagged but no geographic exposure specified',
                severity: 'low',
                suggestedFix: 'Add MAS geographic exposure tag',
                lastUpdated: holding.updated_at ? new Date(holding.updated_at).toISOString().split('T')[0] : 'N/A'
              });
            }
          }
        }
      });
    });

    return result;
  }, [holdingsData]);

  const filteredIssues = issues.filter((issue) => {
    const severityMatch = filterSeverity === "all" || issue.severity === filterSeverity;
    const jurisdictionMatch = filterJurisdiction === "all" || issue.jurisdiction === filterJurisdiction;
    return severityMatch && jurisdictionMatch;
  });

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case "high":
        return <XCircle className="h-4 w-4 text-destructive" />;
      case "medium":
        return <AlertTriangle className="h-4 w-4 text-warning" />;
      case "low":
        return <AlertTriangle className="h-4 w-4 text-muted-foreground" />;
      default:
        return <CheckCircle2 className="h-4 w-4 text-success" />;
    }
  };

  const getSeverityBadge = (severity: string) => {
    const variants: Record<string, "destructive" | "warning" | "secondary" | "default"> = {
      high: "destructive",
      medium: "warning",
      low: "secondary",
    };
    return (
      <Badge variant={variants[severity] || "default"} className="capitalize">
        {severity}
      </Badge>
    );
  };

  const highSeverityCount = issues.filter(i => i.severity === "high").length;
  const mediumSeverityCount = issues.filter(i => i.severity === "medium").length;
  const lowSeverityCount = issues.filter(i => i.severity === "low").length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading validation data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">High Severity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <XCircle className="h-6 w-6 text-destructive" />
              <div className="text-3xl font-bold">
                {highSeverityCount}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Critical issues</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Medium Severity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-warning" />
              <div className="text-3xl font-bold">
                {mediumSeverityCount}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Needs attention</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Low Severity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-muted-foreground" />
              <div className="text-3xl font-bold">
                {lowSeverityCount}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Minor issues</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <Select value={filterSeverity} onValueChange={setFilterSeverity}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severities</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterJurisdiction} onValueChange={setFilterJurisdiction}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by jurisdiction" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Jurisdictions</SelectItem>
            <SelectItem value="MAS">MAS</SelectItem>
            <SelectItem value="FATCA">FATCA</SelectItem>
            <SelectItem value="CRS">CRS</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" className="ml-auto">
          Apply Bulk Fixes
        </Button>
      </div>

      {/* Issues Table */}
      <Card>
        <CardHeader>
          <CardTitle>Validation Issues</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredIssues.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 className="h-12 w-12 text-success mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">All Holdings Valid</h3>
              <p className="text-muted-foreground">
                All holdings have been properly tagged and validated. Ready for export.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]"></TableHead>
                  <TableHead>Holding</TableHead>
                  <TableHead>Jurisdiction</TableHead>
                  <TableHead>Issue</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Suggested Fix</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredIssues.map((issue) => (
                  <TableRow key={issue.id}>
                    <TableCell>{getSeverityIcon(issue.severity)}</TableCell>
                    <TableCell className="font-medium">{issue.holding}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-[#0033A0] text-[#0033A0]">
                        {issue.jurisdiction}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{issue.issue}</div>
                        <div className="text-xs text-muted-foreground">{issue.description}</div>
                      </div>
                    </TableCell>
                    <TableCell>{getSeverityBadge(issue.severity)}</TableCell>
                    <TableCell className="text-sm">{issue.suggestedFix}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {issue.lastUpdated}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm">
                        <Wrench className="h-4 w-4 mr-1" />
                        Fix
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
